import { useRef } from "react";
import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import useBusinessResource from "../hooks/useBusinessResource";
import type { BusinessScope } from "../utils/businessResourceFreshness";
import { prepareSalesReturnItemUnits, SalesReturnUnitItem } from "../utils/salesReturnItemUnits";

interface Options extends BusinessScope {
  originalOrderId: number | string | null | undefined;
  active: boolean;
  draft: unknown;
}

export default function useSalesReturnItemUnits({ originalOrderId, active, draft, ...scope }: Options) {
  const currentDraft = useRef(draft);
  currentDraft.current = draft;
  const context = JSON.stringify([scope.userId, scope.companyId, scope.warehouseId, originalOrderId, active]);
  const currentContext = useRef(context);
  currentContext.current = context;
  const source = useBusinessResource<SalesReturnUnitItem[]>({
    ...scope, resource: "sales-return-source-items", parameters: [String(originalOrderId || "")],
    enabled: active && Boolean(originalOrderId), active,
    load: async (signal) => {
      const response = await apiClient.get(API_CONFIG.apiUrl(`/orders/${originalOrderId}/items`), {
        signal, headers: { "X-Company-Id": String(scope.companyId) },
        params: { company_id: scope.companyId, warehouse_id: scope.warehouseId },
      });
      if (!Array.isArray(response.data)) throw new Error("Les unités de la vente d'origine ne sont pas disponibles. Réessayez.");
      return response.data;
    },
  });
  const prepareItems = async <T extends SalesReturnUnitItem>(items: T[]) => {
    const before = currentDraft.current;
    let originals;
    try { originals = await source.refresh({ force: true }); }
    catch (error) {
      if (context !== currentContext.current) throw new Error("Le contexte a changé. Rouvrez le retour avant de l'enregistrer.");
      throw error;
    }
    if (context !== currentContext.current) throw new Error("Le contexte a changé. Rouvrez le retour avant de l'enregistrer.");
    if (before !== currentDraft.current) throw new Error("La saisie a changé pendant la vérification des unités. Enregistrez à nouveau.");
    return prepareSalesReturnItemUnits(items, originals);
  };
  return { ...source, prepareItems };
}
